'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Category, Country } from '@/types';

interface HeroProps {
  categories: Category[];
  countries: Country[];
}

export default function Hero({ categories, countries }: HeroProps) {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  const handleSearch = () => {
    if (selectedCategory && selectedCountry) {
      router.push(`/category/${selectedCategory}?country=${selectedCountry}`);
    } else if (selectedCategory) {
      router.push(`/category/${selectedCategory}`);
    } else if (selectedCountry) {
      router.push(`/country/${selectedCountry}`);
    }
  };

  return (
    <section className="hero-pattern py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Find Trusted Business Services<br />
            <span className="text-[#d4a574]">Across the GCC</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 mb-4">
            Company Formation • Legal • Accounting • IT Services • HR • Consulting
          </p>

          <div className="flex flex-wrap justify-center gap-6 mt-8 mb-12">
            <div className="flex items-center gap-2 text-white">
              <span className="text-[#d4a574] text-xl">✓</span>
              <span className="font-semibold">121 Verified Providers</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <span className="text-[#d4a574] text-xl">🌍</span>
              <span className="font-semibold">6 GCC Countries</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <span className="text-[#d4a574] text-xl">🎁</span>
              <span className="font-semibold">Free Quotes</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 md:p-6 search-bar transition-colors duration-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Service Type</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-[#1e3a5f] dark:focus:border-[#d4a574] focus:outline-none appearance-none bg-white dark:bg-[#0f172a] text-gray-900 dark:text-white transition-colors duration-300"
                >
                  <option value="">All Services</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Country</label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-[#1e3a5f] dark:focus:border-[#d4a574] focus:outline-none appearance-none bg-white dark:bg-[#0f172a] text-gray-900 dark:text-white transition-colors duration-300"
                >
                  <option value="">All GCC Countries</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  className="w-full bg-[#1e3a5f] dark:bg-[#d4a574] text-white dark:text-[#1e3a5f] px-8 py-3 rounded-lg font-semibold hover:bg-[#152a45] dark:hover:bg-[#e8c9a3] transition flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
